import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import CloseIcon from '@/assets/icons/close.svg?react';
import StyledTooltipContainer from '../styled/StyledTooltipContainer';
import { useGetCompressedFilesUrls } from '@/hooks/useFile';
import { getFileUrl } from '@/api/file.api';
import { queryKeys } from '@/queryKeys';
import { useQueries } from '@tanstack/react-query';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import clsx from 'clsx';

interface EstimateBenefitImagesModalProps {
  closeModal: () => void;
  title?: string;
  description?: string;
  accessToken?: string;
  imageKeys: string[];
}

const EstimateBenefitImagesModal = ({
  closeModal,
  title = '',
  description = '',
  imageKeys,
  accessToken,
}: EstimateBenefitImagesModalProps) => {
  const handleDialogClick = useCallback(
    (event: MouseEvent<HTMLDialogElement>) => {
      if (event.target === event.currentTarget) {
        closeModal();
      }
    },
    [closeModal],
  );

  const { data: compressedImages } = useGetCompressedFilesUrls(
    imageKeys,
    accessToken,
  );

  const [selectedImageKeyIndex, setSelectedImageKeyIndex] = useState(0);

  const originalImageQueries = useQueries({
    queries: imageKeys.map((key) => ({
      queryKey: queryKeys.file.byKey(key, true, accessToken),
      queryFn: () => getFileUrl(key, true, accessToken),
      staleTime: Infinity,
    })),
  });

  const selectedImageQuery = originalImageQueries[selectedImageKeyIndex];
  const selectedImageUrl = selectedImageQuery?.data?.url;

  const goToNextImage = useCallback(() => {
    setSelectedImageKeyIndex((prev) =>
      prev < imageKeys.length - 1 ? prev + 1 : 0,
    );
  }, [imageKeys.length]);

  const goToPreviousImage = useCallback(() => {
    setSelectedImageKeyIndex((prev) =>
      prev > 0 ? prev - 1 : imageKeys.length - 1,
    );
  }, [imageKeys.length]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModal();
      } else if (event.key === 'ArrowRight') {
        goToNextImage();
      } else if (event.key === 'ArrowLeft') {
        goToPreviousImage();
      }
    },
    [closeModal, goToNextImage, goToPreviousImage],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const shouldHideImageArea = useMemo(
    () =>
      selectedImageQuery?.isSuccess === true &&
      selectedImageQuery.data === null,
    [selectedImageQuery?.data, selectedImageQuery?.isSuccess],
  );

  return (
    <dialog
      open
      onClick={handleDialogClick}
      className="fixed left-0 top-0 z-50 flex h-full w-full items-center justify-center overflow-auto bg-black/50"
    >
      <Card className="w-[95vw] md:w-[92vw] lg:w-[90vw] max-w-[1360px] max-h-full overflow-y-auto rounded-lg border border-gray-200">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl leading-6 font-semibold text-gray-950">
              {title}
            </CardTitle>
            <StyledTooltipContainer tooltipMessage="Fermer" asChild>
              <CloseIcon
                onClick={closeModal}
                className="hover:cursor-pointer"
              />
            </StyledTooltipContainer>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-8">
          <div className="flex flex-col gap-2 w-full items-center">
            <div
              className={clsx(
                'relative flex w-full items-center justify-center overflow-hidden h-96 lg:h-[500px] 2xl:h-[600px]',
                { hidden: shouldHideImageArea },
              )}
            >
              {selectedImageUrl ? (
                <img
                  key={selectedImageKeyIndex}
                  src={selectedImageUrl}
                  alt=""
                  className="rounded-lg max-h-full max-w-full object-contain"
                  onClick={closeModal}
                />
              ) : (
                <div className="h-full w-full" aria-hidden />
              )}
              {imageKeys.length > 1 && (
                <>
                  <ChevronLeftIcon
                    className="size-10 md:size-20 stroke-1 stroke-white drop-shadow absolute top-1/2 left-0 md:left-4 -translate-y-1/2 text-gray-800 hover:cursor-pointer z-10"
                    onClick={goToPreviousImage}
                  />
                  <ChevronRightIcon
                    className="size-10 md:size-20 stroke-1 stroke-white drop-shadow absolute top-1/2 right-0 md:right-4 -translate-y-1/2 text-gray-800 hover:cursor-pointer z-10"
                    onClick={goToNextImage}
                  />
                </>
              )}
            </div>
            <div className="flex w-full flex-row gap-2 overflow-x-auto">
              {compressedImages?.map((image, index) => (
                <img
                  key={imageKeys[index] ?? `thumbnail-${index}`}
                  src={image.url}
                  alt=""
                  className={clsx(
                    'rounded-lg w-auto h-10 lg:h-16 hover:cursor-pointer shrink-0',
                    {
                      'border-3 border-gray-800':
                        selectedImageKeyIndex === index,
                    },
                  )}
                  onClick={() => {
                    setSelectedImageKeyIndex(index);
                  }}
                />
              ))}
            </div>
          </div>
          <p className="text-sm leading-6 font-normal text-gray-800 whitespace-pre-wrap max-w-full wrap-break-word w-full">
            {description}
          </p>
        </CardContent>
      </Card>
    </dialog>
  );
};

export default EstimateBenefitImagesModal;
